import React, { useCallback, useEffect, useState } from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import MainButton from '@/components/UI/mobile/MainButton';
import { Infos } from '../../styles';
import moment from 'moment';
import Info from '../Info';
import DatePicker from '@/components/UI/mobile/DatePicker';
import axios from 'axios';
import type { ChargingPile } from '@/types/database/chargingPile/chargingPile';
import type { DownloadChargingPileExpenseExcelResponse } from '@/types/api/chargingPile/downloadChargingPileExpenseExcel';

const DownloadHistory: React.FC<{
  piles: ChargingPile[];
  closePage: () => void;
}> = ({ piles, closePage }) => {
  const [selectedPile, setSelectedPile] = useState<ChargingPile | null>(null);
  const [countingType, setCountingType] = useState<
    'counting' | 'daily' | 'monthly'
  >('counting');
  const [selectedStartDate, setSelectedStartDate] = useState<string>(
    moment().startOf('month').format('YYYY-MM-DD')
  );
  const [selectedEndDate, setSelectedEndDate] = useState<string>(
    moment().format('YYYY-MM-DD')
  );

  useEffect(() => {
    if (piles.length > 0) {
      setSelectedPile(piles[0]);
    }
  }, [piles]);

  const handleDownload = useCallback(async () => {
    if (selectedPile) {
      try {
        const res = await axios.get<DownloadChargingPileExpenseExcelResponse>(
          '/ezcon/api/downloadChargingPileExpenseExcel',
          {
            headers: {
              authorization: localStorage.getItem('feathers-jwt'),
            },
            params: {
              entityType: 'parking',
              entityNumber: [selectedPile.parkingNumber],
              dateRange: [selectedStartDate, selectedEndDate],
              countingType,
            },
          }
        );
        const url = res.data.filePath;
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedPile.chargingPileID}_${selectedStartDate}~${selectedEndDate}.xlsx`;
        link.click();
      } catch (e) {
        console.error(e);
      }
    }
  }, [countingType, selectedEndDate, selectedPile, selectedStartDate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          marginBottom: '1rem',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.2rem',
            marginBottom: '1rem',
          }}
        >
          下載歷史資料
        </Typography>
        <Infos>
          <Info
            title="車位編號"
            value={
              <Select
                value={selectedPile?._id || ''}
                disabled={piles.length === 0}
                onChange={(e) => {
                  setSelectedPile(
                    piles?.find((pile) => pile._id === e.target.value) || null
                  );
                }}
              >
                {piles.map((pile) => (
                  <MenuItem key={pile._id} value={pile._id}>
                    {pile.parkingNumber}
                  </MenuItem>
                ))}
              </Select>
            }
          />
        </Infos>
        <Infos>
          <Info
            title="表格單位"
            value={
              <Select
                value={countingType}
                onChange={(e) => {
                  setCountingType(
                    e.target.value as 'counting' | 'daily' | 'monthly'
                  );
                }}
              >
                <MenuItem value="counting">次</MenuItem>
                <MenuItem value="daily">天</MenuItem>
                <MenuItem value="monthly">月</MenuItem>
              </Select>
            }
          />
        </Infos>
        <Infos>
          <Info
            title="開始日期"
            value={
              <DatePicker
                value={moment(selectedStartDate)}
                pickerType="day"
                onChange={(date) =>
                  setSelectedStartDate(date.format('YYYY-MM-DD'))
                }
              />
            }
          />
        </Infos>
        <Infos>
          <Info
            title="結束日期"
            value={
              <DatePicker
                value={moment(selectedEndDate)}
                pickerType="day"
                onChange={(date) =>
                  setSelectedEndDate(date.format('YYYY-MM-DD'))
                }
              />
            }
          />
        </Infos>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: '1rem',
        }}
      >
        <MainButton
          sx={{
            margin: 0,
            '@media (orientation: landscape)': {
              margin: 0,
            },
          }}
          onClick={handleDownload}
        >
          下載
        </MainButton>
        <MainButton
          sx={{
            margin: 0,
            '@media (orientation: landscape)': {
              margin: 0,
            },
          }}
          onClick={closePage}
        >
          取消
        </MainButton>
      </Box>
    </Box>
  );
};

export default DownloadHistory;
